import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AminiiNavbar from "../components/AminiiNavbar";
import AminiiFooter from "../components/AminiiFooter";
import "../css/AminiiOrderPage.css";
import { API_BASE_URL, SERVER_BASE_URL } from "../config/constants";
import { getOptimizedImageUrl } from "../Utils/upload";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const OrderPage = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const userFirstName = localStorage.getItem("userFirstName") || "";
  const [cartItemCount, setCartItemCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [productsCache, setProductsCache] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortOption, setSortOption] = useState("date-newest");
  const [filterStatus, setFilterStatus] = useState("All");
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expandedTracking, setExpandedTracking] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [showNoOrdersPopup, setShowNoOrdersPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmOrder, setConfirmOrder] = useState(null);

  const toggleProducts = (orderId) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const toggleTracking = (orderId) => {
    setExpandedTracking((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const fetchUserDetails = async (userId, userDetailsId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/userdetails/user/${userId}`,
        { headers: getAuthHeader() }
      );
      const userDetailsList = response.data?.data || response.data || [];
      const userDetails = userDetailsId
        ? userDetailsList.find(
            (detail) => detail.userDetailsId === userDetailsId
          )
        : userDetailsList[0];
      return (
        userDetails || {
          fullName: "Unknown User",
          phone: "N/A",
          address: "N/A",
          appartment: null,
          city: "N/A",
          zip: "N/A",
          state: "N/A",
          email: "N/A",
        }
      );
    } catch (err) {
      console.error("Failed to fetch user details:", err);
      return {
        fullName: "Unknown User",
        phone: "N/A",
        address: "N/A",
        appartment: null,
        city: "N/A",
        zip: "N/A",
        state: "N/A",
        email: "N/A",
      };
    }
  };

  useEffect(() => {
    if (!userId) {
      navigate("/login");
      return;
    }

    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (
          !document.querySelector(
            'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
          )
        ) {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.async = true;
          script.onload = () => resolve(true);
          script.onerror = () => resolve(false);
          document.body.appendChild(script);
        } else {
          resolve(true);
        }
      });
    };

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/orders/user/${userId}`,
          { headers: getAuthHeader() }
        );

        const ordersData = response.data?.data || response.data || [];
        const sanitizedOrders = await Promise.all(
          ordersData.map(async (order) => {
            const userDetails = await fetchUserDetails(
              order.userId,
              order.userDetailsId
            );
            return {
              ...order,
              orderId: String(order.orderId || ""),
              userId: order.userId || userId,
              customerName: userDetails.fullName || "Unknown User",
              customerPhone: userDetails.phone || "N/A",
              customerEmail: userDetails.email || "N/A",
              customerAddress: {
                street: userDetails.address || "N/A",
                city: userDetails.city || "N/A",
                state: userDetails.state || "N/A",
                pincode: String(userDetails.zip || "N/A"),
                country: "India",
              },
            };
          })
        );

        setOrders(sanitizedOrders);
        setFilteredOrders(sanitizedOrders);
        await fetchProductDetails(sanitizedOrders);

        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          setError("Failed to load Razorpay SDK. Please try again.");
        }
      } catch (err) {
        // 404 means the user simply has no orders yet — treat as empty, not an error
        if (err.response?.status === 404) {
          setOrders([]);
          setFilteredOrders([]);
        } else {
          setError(
            `Failed to fetch orders: ${err.response?.data?.message || err.message}`
          );
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchProductDetails = async (orders) => {
      const cache = new Map(productsCache);
      for (const order of orders) {
        for (const product of order.orderedProducts) {
          const key = `${product.productId}_${product.size || "N/A"}_${
            order.orderDate
          }`;
          if (!cache.has(key)) {
            try {
              const response = await axios.get(
                `${API_BASE_URL}/products/${product.productId}`,
                { headers: getAuthHeader() }
              );
              cache.set(key, response.data?.data || response.data);
            } catch (err) {
              cache.set(key, {
                productName: `Product ${product.productId}`,
                productImages: [],
              });
            }
          }
        }
      }
      setProductsCache(cache);
    };

    fetchOrders();
  }, [userId, navigate]);

  useEffect(() => {
    const sortedOrders = [...orders].sort((a, b) => {
      const parseDateTime = (date, time) => {
        const dateStr = date || "";
        const timeStr = time || "00:00:00";
        return new Date(`${dateStr} ${timeStr}`);
      };

      const dateA = parseDateTime(a.orderDate, a.orderTime);
      const dateB = parseDateTime(b.orderDate, b.orderTime);

      const isValidA = dateA.toString() !== "Invalid Date";
      const isValidB = dateB.toString() !== "Invalid Date";

      if (sortOption === "date-newest") {
        if (isValidA && isValidB) return dateB - dateA;
        return (Number(b.orderId) || 0) - (Number(a.orderId) || 0);
      } else if (sortOption === "date-oldest") {
        if (isValidA && isValidB) return dateA - dateB;
        return (Number(a.orderId) || 0) - (Number(b.orderId) || 0);
      } else {
        const statusOrder = [
          "PENDING",
          "RECEIVED",
          "SHIPPED",
          "OUT_FOR_DELIVERY",
          "DELIVERED",
        ];
        return (
          statusOrder.indexOf(a.deliveryStatus || "PENDING") -
          statusOrder.indexOf(b.deliveryStatus || "PENDING")
        );
      }
    });

    const filteredOrders =
      filterStatus === "All"
        ? sortedOrders
        : sortedOrders.filter((order) => {
            const st = (order.orderStatus || order.deliveryStatus || "ORDER_PLACED").toUpperCase();
            if (filterStatus === "ACTIVE") {
              return !["DELIVERED", "CANCELLED"].includes(st);
            }
            if (filterStatus === "DELIVERED") {
              return st === "DELIVERED";
            }
            if (filterStatus === "CANCELLED") {
              return st === "CANCELLED";
            }
            return st === filterStatus || (order.deliveryStatus || "").toUpperCase() === filterStatus;
          });

    setFilteredOrders(filteredOrders);
    setShowNoOrdersPopup(
      !loading && filteredOrders.length === 0 && filterStatus !== "All"
    );
  }, [orders, filterStatus, sortOption, loading]);


  const handlePayNow = async (order) => {
    if (!order.razorpayOrderId) {
      alert(
        "This order does not have a valid payment ID. Please contact support."
      );
      return;
    }
    setConfirmOrder(order);
    setShowConfirmPopup(true);
  };

  const confirmPayment = async () => {
    if (!confirmOrder?.razorpayOrderId) {
      alert("Invalid payment order. Please try again or contact support.");
      setShowConfirmPopup(false);
      setConfirmOrder(null);
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/razorpay-key`,
        { headers: getAuthHeader() }
      );
      const keyId = response.data;

      const options = {
        key: keyId,
        amount: (confirmOrder.totalPrice * 100).toString(),
        currency: "INR",
        name: "AMINII Clothing",
        description: `Payment for Order #${confirmOrder.orderId}`,
        order_id: confirmOrder.razorpayOrderId,
        handler: async (response) => {
          try {
            await axios.post(
              `${SERVER_BASE_URL}/verify-payment`,
              {
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                paymentMethod: response.payment_method || "unknown",
                createdAt: Math.floor(Date.now() / 1000),
              },
              { headers: getAuthHeader() }
            );
            alert("Payment successful! Order updated.");
            setOrders((prev) =>
              prev.map((o) =>
                o.orderId === confirmOrder.orderId
                  ? {
                      ...o,
                      paymentStatus: "PAID",
                      paymentMethod: response.payment_method,
                      paymentDateTime: new Date().toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }),
                    }
                  : o
              )
            );
          } catch (err) {
            alert(
              `Payment verification failed: ${
                err.response?.data || err.message
              }`
            );
          }
        },
        modal: {
          ondismiss: async () => {
            try {
              await axios.post(
                `${SERVER_BASE_URL}/payment-failed`,
                {
                  razorpayOrderId: confirmOrder.razorpayOrderId,
                  razorpayPaymentId: "pay_manual_cancel",
                  error_description: "Payment cancelled by user",
                },
                { headers: getAuthHeader() }
              );
              alert("Payment cancelled by user.");
              setOrders((prev) =>
                prev.map((o) =>
                  o.orderId === confirmOrder.orderId
                    ? {
                        ...o,
                        paymentStatus: "FAILED",
                        paymentAttempts: o.paymentAttempts + 1,
                      }
                    : o
                )
              );
            } catch (err) {
              alert(
                `Error recording cancellation: ${
                  err.response?.data || err.message
                }`
              );
            }
          },
        },
        prefill: {
          name: confirmOrder.customerName || "",
          contact: confirmOrder.customerPhone || "",
          email: confirmOrder.customerEmail || "",
        },
        theme: {
          color: "#F37254",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", async (response) => {
        try {
          await axios.post(
            `${SERVER_BASE_URL}/payment-failed`,
            {
              razorpayOrderId: response.error.metadata.order_id,
              razorpayPaymentId: response.error.metadata.payment_id,
              error_description: response.error.description,
            },
            { headers: getAuthHeader() }
          );
          alert(`Payment failed: ${response.error.description}`);
          setOrders((prev) =>
            prev.map((o) =>
              o.orderId === confirmOrder.orderId
                ? {
                    ...o,
                    paymentStatus: "FAILED",
                    paymentAttempts: o.paymentAttempts + 1,
                  }
                : o
            )
          );
        } catch (err) {
          alert(
            `Error recording payment failure: ${
              err.response?.data || err.message
            }`
          );
        }
      });
      rzp.open();
    } catch (err) {
      alert(`Failed to initiate payment: ${err.response?.data || err.message}`);
    } finally {
      setShowConfirmPopup(false);
      setConfirmOrder(null);
    }
  };

  const cancelPayment = () => {
    setShowConfirmPopup(false);
    setConfirmOrder(null);
  };

  const statusSteps = [
    { key: "ORDER_PLACED", label: "Placed" },
    { key: "ORDER_CONFIRMED", label: "Confirmed" },
    { key: "READY_TO_SHIP", label: "Ready to Ship" },
    { key: "IN_TRANSIT", label: "In Transit" },
    { key: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
    { key: "DELIVERED", label: "Delivered" },
  ];

  return (
    <div className="op-page">
      <AminiiNavbar
        isLoggedIn={!!userId}
        userFirstName={userFirstName}
        cartItemCount={cartItemCount}
        onLogout={() => {
          localStorage.clear();
          navigate("/login");
        }}
      />

      <main className="op-main">
        {loading ? (
          <div className="op-skeleton-container">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="op-skeleton-card skeleton-loading"></div>
            ))}
          </div>
        ) : error ? (
          <div className="op-error">
            {error}
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : filteredOrders.length === 0 && filterStatus === "All" ? (
          <div className="op-empty-state">
            <div className="op-empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
            </div>
            <h3 className="op-empty-title">No orders yet</h3>
            <p className="op-empty-subtitle">Looks like you have not placed any orders. Start exploring our collection!</p>
            <button
              className="op-shop-now-btn"
              onClick={() => navigate("/productList")}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="op-top-bar">
              {/* Luxury Filter Pills */}
              <div className="op-filter-pills">
                <button
                  type="button"
                  className={`op-filter-pill ${filterStatus === "All" ? "active" : ""}`}
                  onClick={() => setFilterStatus("All")}
                >
                  All ({orders.length})
                </button>
                <button
                  type="button"
                  className={`op-filter-pill ${filterStatus === "ACTIVE" ? "active" : ""}`}
                  onClick={() => setFilterStatus("ACTIVE")}
                >
                  In Progress ({orders.filter((o) => !["DELIVERED", "CANCELLED"].includes((o.orderStatus || o.deliveryStatus || "ORDER_PLACED").toUpperCase())).length})
                </button>
                <button
                  type="button"
                  className={`op-filter-pill ${filterStatus === "DELIVERED" ? "active" : ""}`}
                  onClick={() => setFilterStatus("DELIVERED")}
                >
                  Delivered ({orders.filter((o) => (o.orderStatus || o.deliveryStatus || "").toUpperCase() === "DELIVERED").length})
                </button>
                <button
                  type="button"
                  className={`op-filter-pill ${filterStatus === "CANCELLED" ? "active" : ""}`}
                  onClick={() => setFilterStatus("CANCELLED")}
                >
                  Cancelled ({orders.filter((o) => (o.orderStatus || o.deliveryStatus || "").toUpperCase() === "CANCELLED").length})
                </button>
              </div>

              <div className="op-sort-wrapper">
                <select
                  className="op-sort-select"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  aria-label="Sort orders"
                >
                  <option value="date-newest">Sort: Newest</option>
                  <option value="date-oldest">Sort: Oldest</option>
                  <option value="status">Sort: Status</option>
                </select>
              </div>
            </div>

            <div className="op-order-list">
              {filteredOrders.map((order) => {
                const isExpanded = expandedOrders[order.orderId] || false;
                const isTrackingOpen = expandedTracking[order.orderId] || false;
                const status = (order.orderStatus || order.deliveryStatus || "ORDER_PLACED").toUpperCase();
                const isCancelled = status === "CANCELLED";
                const isReadyToShip = status === "READY_TO_SHIP";
                const isInTransit = ["IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"].includes(status);
                const currentStatusIndex = statusSteps.findIndex((s) => s.key === status);
                const currentStepObj = statusSteps[currentStatusIndex] || { label: status.replace(/_/g, " ") };

                return (
                  <div
                    key={order.orderId || `order-${Math.random()}`}
                    className={`op-order-card ${isCancelled ? "op-terminal" : ""}`}
                  >
                    {/* Top Order Card Header */}
                    <div className="op-order-header">
                      <div className="op-order-header-main">
                        <div className="op-order-id-group">
                          <span className="op-order-num">ORDER #{order.orderId || "—"}</span>
                          {order.orderDate && (
                            <span className="op-order-date">Placed on {order.orderDate}</span>
                          )}
                        </div>
                        <div className="op-order-price-group">
                          <span className="op-order-total">₹{(order.totalPrice || 0).toFixed(2)}</span>
                          <span className={`op-status-badge op-status-${status.toLowerCase()}`}>
                            {status === "READY_TO_SHIP" ? " Ready to Ship" : status === "DELIVERED" ? "✓ Delivered" : status === "CANCELLED" ? "✕ Cancelled" : status.replace(/_/g, " ")}
                          </span>
                        </div>
                      </div>

                      {order.paymentStatus === "PENDING" &&
                        order.paymentAttempts < 3 &&
                        (order.paymentType === "ONLINE_PAYMENT" || order.paymentType === "UPI") && (
                          <button
                            className="op-pay-now-btn"
                            onClick={() => handlePayNow(order)}
                          >
                            Complete Payment (₹{(order.totalPrice || 0).toFixed(2)})
                          </button>
                        )}
                    </div>

                    {/* Order Body Details */}
                    <div className="op-order-body">
                      {isReadyToShip && (
                        <div className="op-alert-banner op-alert-info">
                          <strong>Packing & Preparing:</strong> Your designer items are being inspected and prepared for dispatch.
                        </div>
                      )}

                      {isCancelled && (
                        <div className="op-alert-banner op-alert-cancelled">
                          ✕ <strong>Cancelled:</strong> {order.cancellation?.reason || "This order was cancelled."}
                        </div>
                      )}

                      {/* Products Summary & Preview */}
                      <div className="op-products-section">
                        {(() => {
                          const allProducts = order.orderedProducts || [];
                          const isProductsExpanded = expandedProducts[order.orderId] || false;
                          const PREVIEW_COUNT = 2;
                          const visibleProducts = isProductsExpanded ? allProducts : allProducts.slice(0, PREVIEW_COUNT);
                          const hiddenCount = allProducts.length - PREVIEW_COUNT;

                          return (
                            <>
                              <div className="op-products-preview">
                                {visibleProducts.map((product, index) => {
                                  const key = `${product.productId}_${product.size || "N/A"}_${order.orderDate}`;
                                  const productData = productsCache.get(key) || {
                                    productName: `Product ${product.productId}`,
                                    productImages: [],
                                  };
                                  return (
                                    <div key={index} className="op-product-item">
                                      <img
                                        src={productData.productImages[0] ? getOptimizedImageUrl(productData.productImages[0], { width: 120, colorSpace: "srgb" }) : "https://via.placeholder.com/60"}
                                        alt={productData.productName}
                                        className="op-product-image"
                                        loading="lazy"
                                        decoding="async"
                                        onClick={() => navigate(`/productDetails/${product.productId}`)}
                                        onError={(e) => { e.target.src = "https://via.placeholder.com/60"; }}
                                      />
                                      <div className="op-product-details">
                                        <h4
                                          className="op-product-name"
                                          onClick={() => navigate(`/productDetails/${product.productId}`)}
                                        >
                                          {productData.productName}
                                        </h4>
                                        <div className="op-product-meta-row">
                                          {product.size && <span className="op-meta-pill">Size: {product.size}</span>}
                                          {product.quantity && <span className="op-meta-pill">Qty: {product.quantity}</span>}
                                          <span className="op-meta-price">₹{(product.unitPrice || 0).toFixed(2)}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {allProducts.length > PREVIEW_COUNT && (
                                <button
                                  type="button"
                                  className="op-show-more-btn"
                                  onClick={() => toggleProducts(order.orderId)}
                                >
                                  {isProductsExpanded
                                    ? "▲ Show less"
                                    : `▼ +${hiddenCount} more item${hiddenCount > 1 ? "s" : ""} in this order`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {/* Delivery Address Row */}
                      {order.customerName && order.customerName !== "Unknown User" && (
                        <div className="op-delivery-address-box">
                          {/* <span className="op-address-icon">📍</span> */}
                          <div className="op-address-text">
                            <strong>Delivering to {order.customerName}</strong>
                            {order.customerAddress?.street && order.customerAddress.street !== "N/A" && (
                              <span>, {order.customerAddress.street}, {order.customerAddress.city} – {order.customerAddress.pincode}</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Collapsible Shipment Journey Accordion */}
                      {!isCancelled && (
                        <div className="op-tracking-dropdown-card">
                          <button
                            type="button"
                            className="op-tracking-dropdown-toggle"
                            onClick={() => toggleTracking(order.orderId)}
                            aria-expanded={isTrackingOpen}
                          >
                            <div className="op-toggle-left">
                              <span className="op-tracking-pulse-dot" />
                              <span className="op-toggle-status-label">
                                Status: <strong>{currentStepObj.label}</strong>
                              </span>
                            </div>
                            <span className="op-toggle-arrow">
                              {isTrackingOpen ? "Hide Tracking ▲" : "Track Journey ▼"}
                            </span>
                          </button>

                          {isTrackingOpen && (
                            <div className="op-tracking-journey-content">
                              {/* Direct Shiprocket / AWB Link if available */}
                              {(order.shiprocket?.trackingUrl || (order.shiprocket?.awbCode && (isReadyToShip || isInTransit))) && (
                                <div className="op-courier-banner">
                                  <div>
                                    <div className="op-courier-title">Shipped via {order.shiprocket?.courierName || "Courier Partner"}</div>
                                    {order.shiprocket?.awbCode && (
                                      <div className="op-courier-awb">AWB: <strong>{order.shiprocket.awbCode}</strong></div>
                                    )}
                                  </div>
                                  <a
                                    href={order.shiprocket?.trackingUrl || `https://shiprocket.co/tracking/${order.shiprocket?.awbCode}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="op-external-track-btn"
                                  >
                                    Live Courier Tracking ↗
                                  </a>
                                </div>
                              )}

                              {/* Vertical Stepper Timeline */}
                              <div className="op-timeline-vertical">
                                {statusSteps.map((step, index) => {
                                  const isCompleted = currentStatusIndex > index;
                                  const isCurrent = currentStatusIndex === index;
                                  return (
                                    <div
                                      key={step.key}
                                      className={`op-timeline-node ${isCompleted ? "completed" : ""} ${isCurrent ? "current" : ""}`}
                                    >
                                      <div className="op-node-icon-wrap">
                                        <span className="op-node-dot">
                                          {isCompleted ? "✓" : index + 1}
                                        </span>
                                        {index < statusSteps.length - 1 && <span className="op-node-line" />}
                                      </div>
                                      <div className="op-node-content">
                                        <div className="op-node-title">{step.label}</div>
                                        {isCurrent && (
                                          <div className="op-node-sub">Current status of your package</div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Order Footer with Payment Tag & Track Button */}
                    <div className="op-order-footer">
                      <div className="op-payment-tag">
                        <span className="op-payment-mode">
                          {order.paymentType === "ONLINE_PAYMENT" ? "Online Payment" : order.paymentType === "UPI" ? "UPI" : order.paymentType || "Payment"}
                        </span>
                        <span className={`op-payment-status-pill ${order.paymentStatus === "PAID" ? "paid" : "pending"}`}>
                          {order.paymentStatus === "PAID" ? "● Paid" : "● Payment Pending"}
                        </span>
                      </div>

                      {(() => {
                          const shiprocketUrl = order.shiprocket?.trackingUrl;
                          const awbCode = order.shiprocket?.awbCode;
                          const liveTrackUrl = shiprocketUrl
                            ? shiprocketUrl
                            : awbCode
                            ? `https://shiprocket.co/tracking/${awbCode}`
                            : null;

                          if (liveTrackUrl) {
                            return (
                              <a
                                href={liveTrackUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="op-quick-track-link"
                              >
                                Live Courier Tracking ↗
                              </a>
                            );
                          }
                          return (
                            <button
                              type="button"
                              className="op-quick-track-link"
                              onClick={() => navigate(`/track?orderId=${order.orderId}`)}
                            >
                              Track Order ↗
                            </button>
                          );
                        })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      <AminiiFooter />
    </div>
  );
};

export default OrderPage;
